/**
 * Shape we read out of an axios-style error: `error.response.data` may carry a
 * Laravel-format error envelope. Everything is optional because we have no
 * runtime guarantees — `extractApiError` narrows field-by-field at use sites.
 */
interface ApiErrorPayload {
  message?: unknown;
  error?: unknown;
  errors?: Record<string, unknown> | unknown;
}

interface AxiosLikeError {
  response?: {
    data?: ApiErrorPayload;
  };
  message?: string;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Messages that only say "validation failed": they name no reason a person can
 * act on. AuthController sent `message: 'Validation error'` with the real
 * reason in `error`, four sibling controllers sent `message: 'Validation
 * failed'` beside `errors`, and Laravel 8 and older said "The given data was
 * invalid." with the reasons in `errors`.
 *
 * The server no longer sends any of them: every such answer now carries the
 * real reason in `message` too, and `server/tests/Feature/
 * ValidationAnswerCarriesReasonTest.php` fails if one comes back. The list
 * stays for a build that meets a server from before that change, so do not
 * remove it.
 */
const PLACEHOLDER_MESSAGES = new Set([
  'Validation error',
  'Validation failed',
  'The given data was invalid.',
]);

/**
 * The server's own words for a failed request, read from `response.data`, or
 * null when it sent none a person can act on. ONE order, shared by
 * `extractApiError` and `getAxiosErrorMessage` (utils/error.ts) so a screen
 * says the same thing whichever helper its code calls:
 *
 *   1. `errors` (Laravel validation): the first field's first message;
 *   2. `message`, unless it is a placeholder above. The app's envelope is
 *      `{ error: '<code or short title>', message: '<the sentence>' }`
 *      (`error: 'email_verification_required'`, `error: 'WAITLIST'`), so a
 *      real `message` beats `error`;
 *   3. `error`: AuthController's validation answers (register, login,
 *      changeUnverifiedEmail...) carry the reason here beside the placeholder.
 */
export function serverErrorMessage(data: unknown): string | null {
  if (!isObject(data)) return null;
  const resp = data as ApiErrorPayload;

  // Laravel validation errors: { message: "...", errors: { field: ["error1"] } }
  if (isObject(resp.errors)) {
    const firstFieldErrors = Object.values(resp.errors as Record<string, unknown>).flat();
    if (
      firstFieldErrors.length > 0 &&
      typeof firstFieldErrors[0] === 'string' &&
      firstFieldErrors[0]
    ) {
      return firstFieldErrors[0];
    }
  }

  if (typeof resp.message === 'string' && resp.message && !PLACEHOLDER_MESSAGES.has(resp.message)) {
    return resp.message;
  }
  if (typeof resp.error === 'string' && resp.error) return resp.error;
  return null;
}

export function extractApiError(error: unknown, fallback = 'Something went wrong.'): string {
  const resp = isObject(error) ? (error as AxiosLikeError).response?.data : undefined;
  return serverErrorMessage(resp) ?? fallback;
}

/**
 * The server's own sentence when the request was REFUSED on purpose (the
 * app's envelope: `{ success: false, error: '<code>', message }` with a 4xx),
 * or null when it simply failed (no response, a 5xx, or an envelope with no
 * refusal code). A refusal is shown in the server's words and is not worth
 * retrying; a failure gets fixed copy and a "Try again" (components/ui/LoadError).
 */
export function apiRefusalMessage(error: unknown): string | null {
  if (!isObject(error)) return null;
  const response = (error as { response?: { status?: unknown; data?: unknown } }).response;
  if (!response || !isObject(response.data)) return null;
  if (typeof response.status === 'number' && response.status >= 500) return null;
  const data = response.data as ApiErrorPayload;
  if (typeof data.error !== 'string' || !data.error) return null;
  return typeof data.message === 'string' && data.message ? data.message : null;
}
